import { defineStore } from 'pinia';
import { PRODUCTS_API } from '@/api/productsApi';
import { formatDate, formatDateShort } from '@/utils/dateFormatter';

export const useProductsStore = defineStore('products', {
    state: () => ({
        products: [],
        productsHistory: [],
        productItems: [],
        productAlone: '',
        totalProducts: 0,
        productsHistoryTotal: 0,
        loading: false,
        loadingProducts: false,
        loadingProductsHistory: false,
        error: null,
        // Cache tracking
        _fetchCache: null,
        _lastFetchHash: null
    }),

    getters: {
        hasProducts: (state) => state.products.length > 0,
        isEmpty: (state) => state.products.length === 0 && !state.loading,
        getProductById: (state) => (id) => state.products.find(p => p.product_id === id),
        getProductsByCategory: (state) => (categoryId) => state.products.filter(p => p.category_id === categoryId),
        getAvailableProducts: (state) => state.products.filter(p => p.availability_id === 1)
    },

    actions: {

        _getCacheKey(branchId, page, itemsPerPage, search) {
            return JSON.stringify({ branchId, page, itemsPerPage, search });
        },

        _transformProduct(product = {}) {

            if (!product || typeof product !== 'object') {
                return {
                    display_product_name: '',
                    display_base_price: '₱0',
                    display_cost_estimate: '₱0',
                    product_id: null,
                    product_name: '',
                    base_price: 0,
                    cost_estimate: 0,
                    temp_id: null,
                    temp_label: '',
                    size_id: null,
                    size_label: '',
                    category_id: null,
                    category_label: '',
                    availability_id: null,
                    availability_label: '',
                    station_id: null,
                    station_name: '',
                    updatedAtFormatted: '',
                    updatedAtShort: '',
                };
            }

            return {
                display_product_name: `${product.product_name || ''}${product.size_label || ''}${product.temp_label || ''}`,
                display_base_price: `₱${product.base_price ?? 0}`,
                display_cost_estimate: `₱${product.cost_estimate ?? 0}`,
                product_id: product.product_id,
                product_name: product.product_name,
                base_price: product.base_price,
                cost_estimate: product.cost_estimate,
                temp_id: product.temp_id,
                temp_label: product.temp_label,
                size_id: product.size_id,
                size_label: product.size_label,
                category_id: product.category_id,
                category_label: product.category_label,
                station_id: product.station_id,
                station_name: product.station_name,
                availability_id: product.availability_id,
                availability_label: product.availability_label,
                updatedAtFormatted: product.updated_at ? formatDate(product.updated_at) : '',
                updatedAtShort: product.updated_at ? formatDateShort(product.updated_at) : '',
                ...product
            };
        },

        _transformProductsHistory(productHistory = {}) {
            if (!productHistory || typeof productHistory !== 'object') {
                return {
                    display_product_name: '',
                    product_name: '',
                    updatedAtFormatted: '',
                    updatedAtShort: '',
                };
            }

            return {
                display_product_name: `${productHistory.product_name || ''}${productHistory.size_label || ''}${productHistory.temp_label || ''}`,
                product_name: productHistory.product_name,
                updatedAtFormatted: productHistory.updated_at ? formatDate(productHistory.updated_at) : '',
                updatedAtShort: productHistory.updated_at ? formatDateShort(productHistory.updated_at) : '',
                ...productHistory
            };
        },

        clearError() {
            this.error = null;
        },

        _enrichProductWithLabels(product) {
            const existing = this.products.find(p => p.product_id === product.product_id);

            return {
                ...product,
                product_id: existing.product_id,
                display_product_name: `${product.product_name}${existing.size_label}${existing.temp_label}`,
                display_base_price: `₱${product.base_price}`,
                display_cost_estimate: `₱${product.cost_estimate ?? 0}`,
                temp_id: product.temp_id || existing?.temp_id || '',
                temp_label: product.temp_label || existing?.temp_label || '',
                size_id: product.size_id || existing?.size_id || null,
                size_label: product.size_label || existing?.size_label || '',
                category_id: product.category_id || existing?.category_id || null,
                category_label: product.category_label || existing?.category_label || '',
                station_id: product.station_id || existing?.station_id || null,
                station_name: product.station_name || existing?.station_name || '',
                availability_id: product.availability_id || existing?.availability_id || 0,
                availability_label: product.availability_label || existing?.availability_label || '',
                updatedAtFormatted: product.updated_at ? formatDate(product.updated_at) : existing?.updatedAtFormatted || '',
                updatedAtShort: product.updated_at ? formatDateShort(product.updated_at) : existing?.updatedAtShort || '',
            };
        },

        async fetchAllProductsStore({ branchId, page = 1, itemsPerPage = 10, search = '' }) {
            if (!branchId) {
                this.error = 'Branch ID is required';
                return;
            }

            const cacheKey = this._getCacheKey(branchId, page, itemsPerPage, search);

            if (this._lastFetchHash === cacheKey && this.products.length > 0) {
                return;
            }

            this.loadingProducts = true;
            this.error = null;

            try {
                const response = await PRODUCTS_API.fetchAllProductsApi({
                    branchId,
                    page,
                    itemsPerPage,
                    search
                });

                if (!response?.success) {
                    throw new Error(response?.message || 'Failed to fetch products');
                }

                let rawProducts = Array.isArray(response.data) ? response.data : [];
                const totalCount = response.total ?? rawProducts.length;

                const beforeLen = rawProducts.length;
                rawProducts = rawProducts.filter(p => p && typeof p === 'object');
                if (rawProducts.length !== beforeLen) {
                    console.warn('[productsStore] removed invalid items from API response', {
                        before: beforeLen,
                        after: rawProducts.length,
                        branchId,
                        search
                    });
                }

                this.products = rawProducts.map(p => this._transformProduct(p));
                this.totalProducts = totalCount;
                this._lastFetchHash = cacheKey;
                this._fetchCache = { rawProducts, totalCount };

            } catch (error) {
                this.error = error.message || 'Failed to fetch products';
                this.products = [];
                this.totalProducts = 0;
                this._lastFetchHash = null;
                throw error;
            } finally {
                this.loadingProducts = false;
            }
        },

        async fetchTotalProductsCountStore(branchId) {
            this.loading = true;
            this.error = null;
            try {
                const response = await PRODUCTS_API.fetchTotalProductsCountApi(branchId);
                if (response?.status === true) {
                    this.productsOnly = response.data;
                } else {
                    throw new Error(response?.message || 'Failed to fetch products count');
                }
            } catch (error) {
                this.error = error.message || 'Failed to fetch products count';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchProductIngredientsStore(productId) {
            this.loading = true;
            this.error = null;
            try {
                const response = await PRODUCTS_API.fetchProductIngredientsApi(productId);
                if (response?.status === true) {
                    this.productItems = response.data;
                } else {
                    throw new Error('Failed to fetch product ingredients');
                }
            } catch (error) {
                this.error = error.message || 'Failed to fetch product ingredients';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchProductsHistoryStore({ branchId, page = 1, itemsPerPage = 10, search = '' }) {
            if (!branchId) {
                this.error = 'Branch ID is required';
                return;
            }

            const cacheKey = this._getCacheKey(branchId, page, itemsPerPage, search);

            if (this._lastFetchHash === cacheKey && this.products.length > 0) {
                return;
            }

            this.loadingProductsHistory = true;
            this.error = null;

            try {
                const response = await PRODUCTS_API.fetchProductsHistoryApi({
                    branchId,
                    page,
                    itemsPerPage,
                    search
                });

                if (!response?.success) {
                    throw new Error(response?.message || 'Failed to fetch modified products history');
                }

                let rawProducts = Array.isArray(response.data) ? response.data : [];
                const totalCount = response.total ?? rawProducts.length;

                const beforeLen = rawProducts.length;
                rawProducts = rawProducts.filter(p => p && typeof p === 'object');
                if (rawProducts.length !== beforeLen) {
                    console.warn('[productsStore] removed invalid items from API response', {
                        before: beforeLen,
                        after: rawProducts.length,
                        branchId,
                        search
                    });
                }

                this.productsHistory = rawProducts.map(p => this._transformProductsHistory(p));
                this.productsHistoryTotal = totalCount;
                this._lastFetchHash = cacheKey;
                this._fetchCache = { rawProducts, totalCount };

            } catch (error) {
                this.error = error.message || 'Failed to fetch products';
                this.productsHistory = [];
                this.productsHistoryTotal = 0;
                this._lastFetchHash = null;
                throw error;
            } finally {
                this.loadingProductsHistory = false;
            }
        },

        async saveProductsStore(products) {
            this.loading = true;
            this.error = null;
            try {
                const response = await PRODUCTS_API.saveProductsApi(products);
                if (response?.status === true) {
                    return response;
                } else {
                    throw new Error('Failed to save products');
                }
            } catch (error) {
                this.error = error.message || 'Failed to save products';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async saveProductIngredientsStore(products) {
            this.loading = true;
            this.error = null;
            try {
                const response = await PRODUCTS_API.saveProductIngredientsApi(products);
                if (response?.status === true) {
                    return response;
                } else {
                    throw new Error('Failed to save product ingredients');
                }
            } catch (error) {
                this.error = error.message || 'Failed to save product ingredients';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async updateProductStore(productData) {
            this.loading = true;
            this.error = null;
            try {
                let response;
                let updatedData;

                // Check if productData is FormData (has image) or regular object
                if (productData instanceof FormData) {
                    // Get product_id from FormData
                    response = await PRODUCTS_API.updateProductApi(productData);
                } else {
                    response = await PRODUCTS_API.updateProductApi(productData);
                }

                if (!response?.success) {
                    throw new Error(response?.message || 'Failed to update product');
                }

                if (response?.success && response?.data) {
                    updatedData = response.data;
                }

                const enrichedProduct = this._enrichProductWithLabels(updatedData);
                const transformed = this._transformProduct(enrichedProduct);
                const index = this.products.findIndex(p => p.product_id === transformed.product_id);
                if (index !== -1) {
                    // Add cache buster
                    transformed.image_version = Date.now();

                    this.products = [
                        ...this.products.slice(0, index),
                        transformed,
                        ...this.products.slice(index + 1)
                    ];
                }
                
                return transformed;

            } catch (error) {
                this.error = error.message || 'Failed to update product';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async updateIngredientStore(productItemData) {
            this.loading = true;
            this.error = null;
            try {
                const response = await PRODUCTS_API.updateIngredientApi(productItemData);
                if (!response?.data) {
                    throw new Error('Invalid server response');
                }
                const updated = response.data;
                const index = this.productItems.findIndex(p => p.product_item_id === updated.product_item_id);
                if (index !== -1) {
                    this.productItems.splice(index, 1, updated);
                } else {
                    this.productItems.push(updated);
                }
                return updated;
            } catch (error) {
                this.error = error.message || 'Failed to update ingredient';
                throw error;
            } finally {
                this.loading = false;
            }
        }
    }
});